import {useMemo} from 'react';
import mapCreatorProfileResponse from '@/utils/mapCreatorProfileResponse';
import {ProfileProps} from '@/types/creatorProfile';
import creatorProfileFetcher, {
  CreatorProfileResponse,
} from '@/api/fetchers/creatorProfile';
import useDataQuery from '@/hooks/use-data-query';

interface UseCreatorProfileQueryParams {
  profileId: string | null;
  brandId?: string;
  include?: string[];
  enabled?: boolean;
  isPublic?: boolean;
}

export function getQueryKey({
  profileId,
  brandId,
  include,
  isPublic = false,
}: UseCreatorProfileQueryParams) {
  const queryKey = ['creatorProfile', profileId, isPublic, brandId];
  if (include) {
    queryKey.push(include.join(','));
  }

  return queryKey;
}

function useCreatorProfileQuery({
  profileId,
  brandId,
  include,
  enabled = true,
  isPublic = false,
}: UseCreatorProfileQueryParams) {
  const queryKey = getQueryKey({isPublic, profileId, brandId, include});

  const response = useDataQuery<CreatorProfileResponse>({
    queryKey,
    queryFn: isPublic
      ? creatorProfileFetcher.publicCreatorProfile
      : creatorProfileFetcher.creatorProfile,
    refetchOnWindowFocus: false,
    cacheTime: Infinity,
    staleTime: Infinity,
    enabled,
    meta: {
      id: profileId,
      brandId,
      include,
    },
  });

  const profile: null | ProfileProps = useMemo(() => {
    if (!response.data) {
      return null;
    }
    return mapCreatorProfileResponse(response.data as ProfileProps);
  }, [response]);

  return {
    ...response,
    profile,
  };
}

export default useCreatorProfileQuery;
